import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Spark } from "@/components/Doodles";

export const NAV_LINKS = [
    { to: "/what-is-pcch", label: "What's Inside?" },
    { to: "/whats-happening", label: "What's Happening" },
    { to: "/membership", label: "Membership" },
    { to: "/work-room", label: "Work Room" },
    { to: "/get-involved", label: "Get Involved" },
    { to: "/about", label: "About" },
    { to: "/visit", label: "Visit" },
];

const Nav = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    return (
        <>
            <header className="fixed inset-x-0 top-0 z-50 border-b-2 border-ink bg-cream/95 backdrop-blur-sm">
                <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3 sm:px-8">
                    <Link to="/" data-testid="nav-logo" className="group flex items-center gap-2" onClick={() => setOpen(false)}>
                        <Spark className="h-7 w-7 text-orange transition-transform duration-300 group-hover:rotate-45" />
                        <span className="leading-none">
                            <span className="block font-display text-2xl tracking-wider text-ink">PCCH</span>
                            <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-ink/60">
                                Panther City Clubhouse
                            </span>
                        </span>
                    </Link>

                    <nav className="hidden items-center gap-4 xl:flex" aria-label="Primary">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-link-${l.to.slice(1)}`}
                                className={({ isActive }) =>
                                    `font-display text-[13px] tracking-[0.12em] transition-colors duration-200 ${
                                        isActive ? "text-orange underline decoration-2 underline-offset-4" : "text-ink hover:text-orange"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link to="/account" data-testid="nav-cta-visit" className="btn-stamp !px-5 !py-2 !text-base">
                            Member Login
                        </Link>
                    </nav>

                    <button
                        data-testid="nav-menu-toggle"
                        className="flex h-11 w-11 items-center justify-center border-2 border-ink bg-mustard hard-shadow xl:hidden"
                        onClick={() => setOpen((v) => !v)}
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                    >
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        key="mobile-menu"
                        data-testid="nav-mobile-menu"
                        className="fixed inset-0 z-40 flex flex-col bg-forest px-6 pb-10 pt-24"
                        initial={{ opacity: 0, y: -24 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -24 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <nav className="flex flex-1 flex-col justify-center gap-1" aria-label="Mobile">
                            {[{ to: "/", label: "Home" }, ...NAV_LINKS].map((l, i) => (
                                <span key={l.to} className="block overflow-hidden">
                                    <motion.span
                                        className="block"
                                        initial={{ y: "110%" }}
                                        animate={{ y: "0%" }}
                                        transition={{ duration: 0.5, delay: 0.05 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                                    >
                                        <NavLink
                                            to={l.to}
                                            data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                                            onClick={() => setOpen(false)}
                                            className={({ isActive }) =>
                                                `block py-1.5 font-display text-4xl tracking-wide ${
                                                    isActive ? "text-mustard" : "text-cream hover:text-mustard"
                                                }`
                                            }
                                        >
                                            {l.label}
                                        </NavLink>
                                    </motion.span>
                                </span>
                            ))}
                        </nav>
                        <Link to="/account" data-testid="mobile-nav-cta-visit" onClick={() => setOpen(false)} className="btn-stamp w-full !bg-mustard !text-ink">
                            Member Login / My Account
                        </Link>
                        <p className="mt-6 text-center font-hand text-2xl text-cream/70 -rotate-2">A gathering place for weirdos.</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Nav;
